import React, { useState } from 'react';
import { Tool } from '../types';
import { GlassCard } from './ui/GlassCard';
import { ExternalLink, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
interface ToolCardProps {
  tool: Tool;
}
export function ToolCard({
  tool
}: ToolCardProps) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const domain = new URL(tool.url).hostname;
  // Multiple logo sources as fallbacks
  const logoSources = [tool.logo, `https://logo.clearbit.com/${domain}`, `https://www.google.com/s2/favicons?domain=${domain}&sz=128`, `https://icons.duckduckgo.com/ip3/${domain}.ico` // DuckDuckGo
  ].filter(Boolean); // Remove null/undefined values
  const currentLogoUrl = logoSources[fallbackIndex];
  const handleLogoError = () => {
    // Try next fallback source
    if (fallbackIndex < logoSources.length - 1) {
      setFallbackIndex(fallbackIndex + 1);
    } else {
      // All sources failed, show emoji
      setLogoError(true);
    }
  };
  // Parse price details into table rows
  const parsePricing = (priceDetails?: string) => {
    if (!priceDetails) return [];
    const tiers = priceDetails.split(',').map(tier => tier.trim());
    return tiers.map(tier => {
      const match = tier.match(/^(.+?)\s+(\$[\d.]+(?:\/mo|\/user\/mo)?|custom|TBA|Free)$/i);
      if (match) {
        return {
          plan: match[1],
          price: match[2]
        };
      }
      return {
        plan: tier,
        price: ''
      };
    });
  };
  const pricingTiers = parsePricing(tool.priceDetails);
  return <GlassCard className="h-full flex flex-col group overflow-hidden border-white/5 transition-colors duration-500" data-id="element-222">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-cyan-500/10 group-hover:via-purple-500/10 group-hover:to-pink-500/10 transition-all duration-500" data-id="element-223" />

      <div className="p-6 flex flex-col h-full relative z-10" data-id="element-224">
        <div className="flex justify-between items-start mb-4" data-id="element-225">
          <div className="flex flex-col gap-2" data-id="element-226">
            {/* Tool Icon - rounded with no background */}
            <div className="w-12 h-12 flex items-center justify-center overflow-hidden rounded-lg" data-id="element-227">
              {!logoError && currentLogoUrl ? <img src={currentLogoUrl} alt={`${tool.name} logo`} className="w-full h-full object-contain" onError={handleLogoError} data-id="element-228" /> : <div className="text-2xl" data-id="element-229">{tool.icon}</div>}
            </div>

            {/* Version badge */}
            {tool.version && <span className="bg-white/5 text-gray-400 text-[10px] font-medium px-2 py-0.5 rounded-full border border-white/10 w-fit" data-id="element-230">
                {tool.version}
              </span>}
          </div>

          <div className="flex items-center gap-2" data-id="element-231">
            {/* Favorite Icon */}
            <button onClick={() => setIsFavorite(!isFavorite)} className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer" data-id="element-232">
              <Heart size={14} className={`transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} data-id="element-233" />
            </button>

            {/* Category Badge */}
            <span className={`
              px-2 py-1 rounded-md text-xs font-medium border
              ${tool.category === 'Image' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : tool.category === 'Video' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : tool.category === 'Audio' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : tool.category === 'Development' ? 'bg-green-500/10 text-green-400 border-green-500/20' : tool.category === 'Writing' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : tool.category === 'Marketing' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : tool.category === 'Design' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-white/5 text-gray-400 border-white/10'}
            `} data-id="element-234">
              {tool.category}
            </span>
          </div>
        </div>

        <div className="mb-2" data-id="element-235">
          <h3 className="text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-purple-400 transition-all duration-300 font-special" data-id="element-236">
            {tool.name}
          </h3>
        </div>

        <p className="text-gray-400 text-sm line-clamp-2 mb-[30px] group-hover:text-gray-300 transition-colors font-sans" data-id="element-237">
          {tool.description}
        </p>

        {/* Mini Pricing Table - with extra spacing */}
        {pricingTiers.length > 0 && <div className="mb-4 bg-white/5 rounded-lg border border-white/5 overflow-hidden" data-id="element-238">
            <div className="divide-y divide-white/5" data-id="element-239">
              {pricingTiers.map((tier, index) => <div key={index} className="flex justify-between items-center px-3 py-2 hover:bg-white/5 transition-colors" data-id="element-240">
                  <span className="text-xs text-gray-400 font-medium font-sans" data-id="element-241">
                    {tier.plan}
                  </span>
                  <span className="text-xs text-green-400 font-bold font-sans" data-id="element-242">
                    {tier.price}
                  </span>
                </div>)}
            </div>
          </div>}

        <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between" data-id="element-243">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-400 font-sans" data-id="element-244">
            {tool.pricing}
          </span>

          <motion.a href={tool.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-bold text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-all cursor-pointer font-sans" whileTap={{
          scale: 0.95
        }} data-id="element-245">
            Visit <ExternalLink size={14} data-id="element-246" />
          </motion.a>
        </div>
      </div>
    </GlassCard>;
}